/**
 * The manage view — an admin section, beside Users and Settings, as the pattern allows.
 *
 * A queue, not an archive: the filter is by status because that is how it gets worked, and status
 * and remark are set together because a manager almost always does both at once. Every change
 * flags the raiser and sends them their copy — the whole point of the pattern is that feedback
 * into a void teaches people not to send any.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Select } from '@/components/ui';
import { ErrorNote, PageHeader } from '@/components/bits';
import { FEEDBACK_CATEGORY, FEEDBACK_STATUS } from '@/components/Feedback';
import type { FeedbackStatus, ManagedFeedback } from '@/lib/types';

const STATUSES: FeedbackStatus[] = ['new', 'in_progress', 'resolved', 'declined'];

export function HrFeedback() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<FeedbackStatus | 'all'>('all');

  const list = useQuery({
    queryKey: ['managed-feedback', status],
    queryFn: () => api.managedFeedback(status === 'all' ? { limit: 100 } : { status, limit: 100 }),
  });
  const digest = useQuery({
    queryKey: ['feedback-pending-digest'],
    queryFn: () => api.pendingDigest(),
  });

  const counts = list.data?.counts ?? {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feedback"
        subtitle="What people have told HR, and what HR told them back."
      />

      {digest.data ? (
        <Alert tone="neutral">
          {digest.data.count === 0
            ? 'Nothing waiting for tomorrow’s 07:00 digest — an empty digest is never sent.'
            : `${digest.data.count} item${digest.data.count === 1 ? '' : 's'} will be in tomorrow’s 07:00 digest. Picking one up now takes it out of that email.`}
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {(['all', ...STATUSES] as const).map((option) => (
          <Button
            key={option}
            size="sm"
            variant={status === option ? 'primary' : 'secondary'}
            onClick={() => setStatus(option)}
          >
            {option === 'all' ? 'All' : FEEDBACK_STATUS[option].label}
            {option !== 'all' && counts[option] !== undefined ? ` (${counts[option]})` : ''}
          </Button>
        ))}
      </div>

      {list.error ? <ErrorNote error={list.error} /> : null}

      {(list.data?.feedback ?? []).length === 0 ? (
        <EmptyState title="Nothing here">
          {status === 'all'
            ? 'Nobody has raised anything yet. The Feedback button beside the account area is where it arrives from.'
            : 'Nothing with that status.'}
        </EmptyState>
      ) : (
        <div className="grid gap-4">
          {(list.data?.feedback ?? []).map((item) => (
            <FeedbackCard
              key={item.id}
              item={item}
              onSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['managed-feedback'] });
                void queryClient.invalidateQueries({ queryKey: ['feedback-pending-digest'] });
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackCard({ item, onSaved }: { item: ManagedFeedback; onSaved: () => void }) {
  const [status, setStatus] = useState<FeedbackStatus>(item.status);
  const [remark, setRemark] = useState(item.admin_remark ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.updateFeedback(item.id, {
        ...(status === item.status ? {} : { status }),
        ...(remark === (item.admin_remark ?? '') ? {} : { admin_remark: remark }),
      }),
    onSuccess: onSaved,
  });

  const changed = status !== item.status || remark !== (item.admin_remark ?? '');

  return (
    // The id is on the wrapper so a card is addressable — by a test, and by anyone reading the
    // DOM to work out which row they are looking at.
    <div data-feedback-id={item.id}>
      <Card
        title={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={FEEDBACK_STATUS[item.status].tone}>
              {FEEDBACK_STATUS[item.status].label}
            </Badge>
            <span className="text-sm font-medium">{item.raised_by}</span>
            <span className="text-muted-foreground text-xs font-normal">
              {FEEDBACK_CATEGORY[item.category]} · {formatInstant(item.created_at)}
              {item.notified_at ? ' · in a digest' : ''}
            </span>
          </span>
        }
      >
        <p className="text-sm whitespace-pre-wrap">{item.body}</p>

        {(item.context.path ?? item.context.reference) ? (
          <p className="text-muted-foreground mt-2 text-xs">
            Raised from <span className="font-mono">{item.context.path}</span>
            {item.context.proposal_id ? ` · proposal ${item.context.proposal_id}` : ''}
            {item.context.claim_id ? ` · claim ${item.context.claim_id}` : ''}
            {item.context.app_version ? ` · v${item.context.app_version}` : ''}
          </p>
        ) : null}

        {save.error ? <ErrorNote error={save.error} /> : null}

        <div className="mt-4 grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
          <Field label="Status">
            {(props) => (
              <Select
                {...props}
                value={status}
                onChange={(event) => setStatus(event.target.value as FeedbackStatus)}
              >
                {STATUSES.map((option) => (
                  <option key={option} value={option}>
                    {FEEDBACK_STATUS[option].label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Reply" hint="The raiser sees this, and gets it by email.">
            {(props) => (
              <Input
                {...props}
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                placeholder="What you found, or what happens next."
              />
            )}
          </Field>
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={!changed}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </div>

        {item.handled_at ? (
          <p className="text-muted-foreground mt-2 text-xs">
            Last handled {formatInstant(item.handled_at)}
          </p>
        ) : null}
      </Card>
    </div>
  );
}
